import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      const response = await fetch('https://readdy.ai/api/form/d6qvdu5sbgrc47fimcf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus(''), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  const navLinks = ['Programs', 'Coaching', 'Facilities', 'About', 'Contact'];

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* ── Navigation ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="w-full px-5 md:px-12 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 shrink-0">
            <img src="https://public.readdy.ai/ai/img_res/cad45f8a-c62c-4331-ae86-9599febd7808.png" alt="Tru Path Golf Academy" className="h-16 md:h-24 w-auto mt-2" />
          </div>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <Link key={link} to="/" className="text-[15px] font-semibold text-[#1B4332] hover:text-[#D4AF37] transition-colors relative group whitespace-nowrap">
                {link}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#D4AF37] group-hover:w-full transition-all duration-300"></span>
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <a
            href="https://fox.tenfore.golf/theranches/shop/memberships/4--trupath-academy"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="hidden lg:block bg-[#EDD84A] text-[#1B4332] px-6 py-3 rounded-full text-[13px] font-bold uppercase tracking-wide hover:scale-105 hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer shrink-0"
          >
            Book Your Free Swing Evaluation
          </a>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-[#1B4332]/10 cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-[#1B4332] text-xl`}></i>
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/98 px-6 pb-6 flex flex-col gap-1 border-t border-gray-100">
            {navLinks.map((link) => (
              <Link
                key={link}
                to="/"
                className="py-3 text-[16px] font-semibold text-[#1B4332] hover:text-[#D4AF37] transition-colors border-b border-gray-100 last:border-0 cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link}
              </Link>
            ))}
            <a
              href="https://fox.tenfore.golf/theranches/shop/memberships/4--trupath-academy"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="mt-4 bg-[#EDD84A] text-[#1B4332] px-6 py-3 rounded-full text-[13px] font-bold uppercase tracking-wide text-center whitespace-nowrap cursor-pointer"
            >
              Book Your Free Swing Evaluation
            </a>
          </div>
        )}
      </nav>

      {/* ── Hero Section ── */}
      <section className="relative min-h-screen mx-3 md:mx-6 mb-6 mt-4 md:mt-6 rounded-[24px] md:rounded-[35px] overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/dfa508f77ab46f4ae3a9f39ccc2171a1.jpg"
            alt="Golf Course"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-[#1B4332]/60"></div>
        </div>

        <div className="relative z-10 min-h-screen flex items-center px-6 md:px-[8%] lg:px-[12%] pt-28 md:pt-24 pb-48 md:pb-52">
          <div className="w-full max-w-[680px]">
            <h1 className="mb-6 md:mb-9 text-left">
              <div className="text-white text-[40px] sm:text-[55px] md:text-[68px] lg:text-[78px] font-bold leading-[0.95] tracking-tight">MASTER YOUR</div>
              <div className="text-[#EDD84A] text-[48px] sm:text-[65px] md:text-[80px] lg:text-[92px] font-serif italic leading-[0.85] mb-4 md:mb-6" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.4)' }}>True Swing &amp;</div>
              <div className="text-white text-[40px] sm:text-[55px] md:text-[68px] lg:text-[78px] font-bold leading-[0.95] tracking-tight">TRANSFORM YOUR GAME</div>
            </h1>
            <p className="text-white/90 text-[16px] md:text-[20px] lg:text-[24px] font-light leading-relaxed max-w-[520px] mb-8 md:mb-11 text-left">
              Elite coaching, state-of-the-art facilities, and personalized training programs designed to elevate golfers of all skill levels
            </p>
            <a
              href="https://fox.tenfore.golf/theranches/shop/memberships/4--trupath-academy"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-block bg-[#EDD84A] text-[#1B4332] px-7 py-4 rounded-full text-[13px] font-bold uppercase tracking-wide hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Book Your Free Swing Evaluation
            </a>
          </div>
        </div>

        {/* Bottom info banner */}
        <div className="absolute bottom-8 md:bottom-16 left-0 right-0 flex justify-center px-4 md:px-10">
          <div className="w-full max-w-[1100px] bg-white/10 backdrop-blur-md border border-white/20 rounded-xl md:rounded-2xl px-5 md:px-10 py-5 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0">

            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EDD84A] shrink-0">
                <i className="ri-calendar-check-line text-[#1B4332] text-lg"></i>
              </div>
              <div>
                <p className="text-white text-[14px] font-bold leading-tight">Train <span className="text-[#EDD84A]">All Year</span></p>
                <p className="text-white/60 text-[10px] uppercase tracking-[1.5px] font-semibold mt-0.5">Never Stop Improving</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 border border-white/20 shrink-0">
                <i className="ri-sun-line text-[#EDD84A] text-lg"></i>
              </div>
              <div>
                <p className="text-white text-[14px] font-bold">On the Course</p>
                <p className="text-white/60 text-[11px] mt-0.5">Beautiful 18 hole golf course and driving range</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 border border-white/20 shrink-0">
                <i className="ri-computer-line text-[#EDD84A] text-lg"></i>
              </div>
              <div>
                <p className="text-white text-[14px] font-bold">Simulators</p>
                <p className="text-white/60 text-[11px] mt-0.5">Train indoors when weather won't cooperate</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 border border-white/20 shrink-0">
                <i className="ri-shield-star-line text-[#EDD84A] text-lg"></i>
              </div>
              <div>
                <p className="text-white text-[14px] font-semibold">Year-Round Edge</p>
                <p className="text-white/60 text-[10px] uppercase tracking-wider mt-0.5">No Off-Season</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Training & Technology Section ── */}
      <section className="bg-white py-16 md:py-28 px-5 md:px-[8%]">
        <div className="max-w-[1300px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

            {/* Left: Text Content */}
            <div>
              <p className="text-[#EDD84A] text-[13px] uppercase tracking-[3px] font-semibold mb-4">TruPath Academy</p>
              <h2 className="text-[36px] md:text-[48px] lg:text-[52px] font-bold text-[#1B1B1B] leading-tight mb-3">
                TRAIN WITH<br />
                <span className="text-[#1B4332] font-serif italic font-normal">Real Data.</span>
              </h2>
              <p className="text-[16px] md:text-[18px] text-[#4A4A4A] font-light leading-relaxed mb-6">
                Don&apos;t lose your edge this winter — build one. Perfect your swing with real data and come spring, be ready to dominate.
              </p>
              <p className="text-[14px] md:text-[15px] text-[#6B6B6B] leading-relaxed mb-8">
                TruPath Academy at The Ranches Golf Club combines expert PGA instruction with cutting-edge swing analysis technology. Every lesson is backed by real data — from launch monitors to video analysis — so you can see exactly what&apos;s happening in your swing and make meaningful improvements faster than ever before.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
                {[
                  { icon: 'ri-bar-chart-2-line', title: 'Launch Monitor Data', desc: 'Real-time ball flight and swing metrics on every shot' },
                  { icon: 'ri-video-line', title: 'Video Analysis', desc: 'Frame-by-frame breakdown of your swing mechanics' },
                  { icon: 'ri-tools-line', title: 'Club Fitting', desc: 'Custom fitting to match your swing and maximize performance' },
                  { icon: 'ri-mental-health-line', title: 'Sports Psychology', desc: 'Mental training to sharpen focus and build confidence' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1B4332]/10 shrink-0">
                      <i className={`${item.icon} text-[#1B4332] text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-[14px] font-bold text-[#2C2C2C] mb-1">{item.title}</p>
                      <p className="text-[13px] text-[#6B6B6B] leading-snug">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#1B4332] rounded-2xl p-6">
                <p className="text-[#EDD84A] text-[12px] uppercase tracking-widest font-semibold mb-3">Mix &amp; Match Units</p>
                <p className="text-white/80 text-[13px] mb-4">Each unit is one of the following — choose what fits your game:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {['Lessons', 'Club Fitting', 'Club Work', 'On-Course Training', 'Clinics', 'Group Fitness'].map((unit) => (
                    <div key={unit} className="bg-white/10 rounded-lg px-3 py-2 text-center">
                      <span className="text-white text-[12px] font-medium">{unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Membership Levels */}
            <div className="flex flex-col gap-5">
              <div className="text-center mb-2">
                <h3 className="text-[22px] font-bold text-[#2C2C2C] uppercase tracking-wide">Membership Levels</h3>
                <p className="text-[14px] text-[#6B6B6B] mt-1">Choose the plan that fits your goals</p>
              </div>

              {[
                {
                  level: 'Level 1',
                  subtitle: 'Includes ONE Unit Per Month',
                  desc: 'Choose One Monthly: Lesson, Club Fitting or Club Work',
                  color: '#2C5F3F',
                  prices: [
                    { label: '3-Month', price: '$110/mo' },
                    { label: '6-Month', price: '$99/mo' },
                    { label: '12-Month', price: '$85/mo' },
                  ],
                },
                {
                  level: 'Level 2',
                  subtitle: 'Includes TWO Units Per Month',
                  desc: 'Choose Two Monthly: Lesson, Club Fitting or Club Work',
                  color: '#1B4332',
                  prices: [
                    { label: 'M-to-M', price: '$220/mo' },
                    { label: '3-Month', price: '$200/mo' },
                    { label: '6-Month', price: '$180/mo' },
                    { label: '12-Month', price: '$150/mo' },
                  ],
                },
                {
                  level: 'Level 3',
                  subtitle: 'Includes THREE Units Per Month',
                  desc: 'Choose Three Monthly: Lesson, Club Fitting or Club Work',
                  color: '#0F2920',
                  prices: [
                    { label: 'M-to-M', price: '$300/mo' },
                    { label: '3-Month', price: '$265/mo' },
                    { label: '6-Month', price: '$215/mo' },
                    { label: '12-Month', price: '$180/mo' },
                  ],
                },
              ].map((tier) => (
                <div key={tier.level} className="rounded-2xl overflow-hidden border border-gray-100">
                  <div className="px-5 py-4" style={{ backgroundColor: tier.color }}>
                    <span className="text-[#EDD84A] text-[13px] font-bold uppercase tracking-widest">{tier.level}</span>
                    <p className="text-white text-[16px] font-bold mt-0.5">{tier.subtitle}</p>
                    <p className="text-white/60 text-[12px] mt-1 leading-snug">{tier.desc}</p>
                  </div>
                  <div className="bg-[#F9F7F4] px-5 py-4 flex gap-2 flex-wrap">
                    {tier.prices.map((p) => (
                      <div key={p.label} className="flex flex-col items-center bg-white rounded-xl px-3 py-3 border border-gray-100 flex-1 min-w-[60px]">
                        <span className="text-[10px] text-[#6B6B6B] uppercase tracking-wide mb-1">{p.label}</span>
                        <span className="text-[15px] font-bold text-[#1B4332]">{p.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <a
                href="https://fox.tenfore.golf/theranches/shop/memberships/4--trupath-academy"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-2 bg-[#EDD84A] text-[#1B4332] px-8 py-4 rounded-full text-[14px] font-bold uppercase tracking-wide text-center hover:scale-105 hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Ready for your first lesson? — Book Now!
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Meet Your Pro Section ── */}
      <section className="py-16 md:py-28 px-5 md:px-[8%]" style={{ background: 'linear-gradient(135deg, #0F2920 0%, #1B4332 60%, #2C5F3F 100%)' }}>
        <div className="max-w-[1300px] mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-[#EDD84A] text-[13px] uppercase tracking-[3px] font-semibold mb-3">Your Instructor</p>
            <h2 className="text-[36px] md:text-[48px] lg:text-[52px] font-bold text-white leading-tight">
              MEET YOUR <span className="font-serif italic font-normal text-[#EDD84A]">Pro</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* Photo */}
            <div className="relative mx-auto w-full max-w-[480px] lg:max-w-none">
              <div className="rounded-[24px] md:rounded-[28px] overflow-hidden w-full" style={{ height: 'clamp(340px, 60vw, 580px)' }}>
                <img
                  src="https://theranchesgolfclub.com/wp-content/uploads/2025/10/image1-846x1024.jpeg"
                  alt="Mason Christison, PGA Associate"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 -right-4 md:-bottom-6 md:-right-6 bg-[#EDD84A] rounded-2xl px-5 py-4 md:px-6 md:py-5">
                <p className="text-[#1B4332] text-[24px] md:text-[28px] font-bold leading-none">6,000+</p>
                <p className="text-[#1B4332] text-[11px] md:text-[12px] font-semibold uppercase tracking-wide mt-1">Lessons Taught</p>
              </div>
            </div>

            {/* Info */}
            <div className="pt-6 lg:pt-0">
              <h3 className="text-[30px] md:text-[40px] font-bold text-white leading-tight mb-1">Mason Christison</h3>
              <p className="text-[#EDD84A] text-[15px] md:text-[16px] font-semibold uppercase tracking-widest mb-2">PGA Associate</p>
              <p className="text-white/60 text-[13px] md:text-[14px] uppercase tracking-wider mb-6 md:mb-8">Head Golf Professional</p>

              <p className="text-white/85 text-[14px] md:text-[15px] leading-relaxed mb-4 md:mb-5">
                Mason Christison has been teaching and coaching golf for over ten years. Born in St. George, Utah, he has been golfing since he could walk and went on to graduate from Southern Utah University with degrees in Outdoor Recreation and Psychology, with an emphasis in sports psychology.
              </p>
              <p className="text-white/85 text-[14px] md:text-[15px] leading-relaxed mb-4 md:mb-5">
                With more than 6,000 lessons taught, Mason brings a wealth of experience to his students. He has earned certificates in coaching and officiating, served as head coach for both boys&apos; and girls&apos; Utah high school teams, caddied professionally at a Golf Digest Top 100 course for PGA Tour members, and is also a PGA associate and certified club fitter.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 md:mb-10">
                {[
                  { icon: 'ri-award-line', text: 'PGA Associate & Certified Club Fitter' },
                  { icon: 'ri-graduation-cap-line', text: 'SUU — Outdoor Recreation & Psychology' },
                  { icon: 'ri-trophy-line', text: 'Head Coach — UT High School Boys & Girls' },
                  { icon: 'ri-user-star-line', text: 'Caddied at Golf Digest Top 100 Course' },
                ].map((cred) => (
                  <div key={cred.text} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 shrink-0 mt-0.5">
                      <i className={`${cred.icon} text-[#EDD84A] text-sm`}></i>
                    </div>
                    <p className="text-white/80 text-[13px] leading-snug">{cred.text}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <a
                  href="https://fox.tenfore.golf/theranches/shop/memberships/4--trupath-academy"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="bg-[#EDD84A] text-[#1B4332] px-6 py-4 rounded-full text-[13px] font-bold uppercase tracking-wide text-center inline-flex items-center justify-center gap-3 hover:scale-105 hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  BOOK A LESSON WITH MASON
                </a>
                <a
                  href="tel:8016589613"
                  className="border border-white/30 text-white px-6 py-4 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-3 hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line"></i>
                  (801) 658-9613
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-[#1C1C1C] rounded-[20px] md:rounded-[30px] mx-3 md:mx-10 lg:mx-16 mb-8 md:mb-16 py-10 md:py-12 px-6 md:px-[7%]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-10">
            {/* Brand */}
            <div>
              <img src="https://public.readdy.ai/ai/img_res/cad45f8a-c62c-4331-ae86-9599febd7808.png" alt="Tru Path Golf Academy" className="h-12 md:h-14 w-auto mb-4" />
              <p className="text-white/60 text-[14px] leading-relaxed">
                Elite golf instruction at The Ranches Golf Club, Eagle Mountain, Utah.
              </p>
            </div>

            {/* Navigate */}
            <div>
              <h4 className="text-white/50 text-[11px] uppercase tracking-widest mb-4 md:mb-5">Navigate</h4>
              <div className="flex flex-col gap-3">
                <Link to="/" className="text-white/75 text-[14px] hover:text-[#D4AF37] transition-colors cursor-pointer">Programs &amp; Pricing</Link>
                <Link to="/" className="text-white/75 text-[14px] hover:text-[#D4AF37] transition-colors cursor-pointer">Our Coaches</Link>
                <Link to="/" className="text-white/75 text-[14px] hover:text-[#D4AF37] transition-colors cursor-pointer">Facilities</Link>
                <a href="https://fox.tenfore.golf/theranches/shop/memberships/4--trupath-academy" target="_blank" rel="nofollow noopener noreferrer" className="text-white/75 text-[14px] hover:text-[#D4AF37] transition-colors cursor-pointer">Book a Lesson</a>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white/50 text-[11px] uppercase tracking-widest mb-4 md:mb-5">Contact</h4>
              <div className="flex flex-col gap-3">
                <a href="tel:8016589613" className="flex items-center gap-2 text-white/75 text-[14px] hover:text-[#D4AF37] transition-colors cursor-pointer">
                  <i className="ri-phone-line text-[#D4AF37]"></i>
                  (801) 658-9613
                </a>
                <a href="mailto:info@golfandfitnessutah.com" className="flex items-center gap-2 text-white/75 text-[14px] hover:text-[#D4AF37] transition-colors cursor-pointer break-all">
                  <i className="ri-mail-line text-[#D4AF37] shrink-0"></i>
                  info@golfandfitnessutah.com
                </a>
                <div className="flex items-start gap-2 text-white/75 text-[14px]">
                  <i className="ri-map-pin-line text-[#D4AF37] mt-0.5 shrink-0"></i>
                  <span>4128 East Clubhouse Lane<br />Eagle Mountain, UT 84005</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/15 pt-6 md:pt-7 flex flex-col md:flex-row items-center gap-4 md:justify-between">
            <div className="flex items-center gap-4">
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#D4AF37]/20 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-white/70 text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#D4AF37]/20 transition-colors cursor-pointer">
                <i className="ri-facebook-line text-white/70 text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#D4AF37]/20 transition-colors cursor-pointer">
                <i className="ri-youtube-line text-white/70 text-sm"></i>
              </a>
            </div>
            <p className="text-white/40 text-[12px] text-center">© 2025 Tru Path Golf Academy. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-white/50 text-[13px] hover:text-white transition-colors whitespace-nowrap cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-white/50 text-[13px] hover:text-white transition-colors whitespace-nowrap cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Newsletter Modal ── */}
      {showNewsletter && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowNewsletter(false)}>
          <div className="bg-white rounded-[24px] p-8 md:p-10 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-[24px] md:text-[28px] font-bold text-[#2C2C2C]">Get Started Today</h3>
              <button onClick={() => setShowNewsletter(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <p className="text-[15px] text-[#6B6B6B] mb-8">Fill out the form below and we&apos;ll contact you to schedule your free consultation.</p>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form id="contact_modal_form">
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-[15px] mb-4 outline-none focus:border-[#D4AF37] transition-colors"
                required
              />
              <button type="submit" className="w-full bg-[#1B4332] text-white py-4 rounded-full text-[14px] font-bold uppercase tracking-wide hover:bg-[#0F2920] transition-colors whitespace-nowrap cursor-pointer">
                SUBMIT REQUEST
              </button>
              {submitStatus === 'success' && (
                <p className="text-[#1B4332] text-sm mt-3 text-center">Thank you! We&apos;ll be in touch soon.</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-500 text-sm mt-3 text-center">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
